import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft, Upload, Image as ImageIcon, Loader2, Plus, Trash2, 
  ChefHat, Sparkles, PenLine, FileText, Camera, X, Link as LinkIcon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { PasswordDialog } from "@/components/PasswordDialog";
import { compressImageFile, isHeicImage, HEIC_HELP } from "@/lib/imageCompress";
import { TagInput } from "@/components/TagInput";
import { ThemeToggle } from "@/components/ThemeToggle";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = ["Desserts", "Breads", "Soups", "Main Dishes", "Sides", "Preserves"];

const emptyIngredient = { name: "", amount_imperial: "", amount_metric: "", notes: "" };

export default function AddRecipePage() {
  const navigate = useNavigate();
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const fileInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState("upload");
  const [isExtracting, setIsExtracting] = useState(false);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      setPasswordDialogOpen(true);
    }
  }, [isAuthenticated]);
  const [isSaving, setIsSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [pastedText, setPastedText] = useState("");
  const [recipeUrl, setRecipeUrl] = useState("");
  const [extractMode, setExtractMode] = useState("image"); // "image", "text", or "url"
  const [recipePhoto, setRecipePhoto] = useState(null); // Saved photo for recipe
  
  const [formData, setFormData] = useState({
    title: "",
    source: "",
    category: "",
    servings: "",
    prep_time: "",
    cook_time: "",
    ingredients: [{ ...emptyIngredient }],
    instructions: [""],
    notes: "",
    image_url: "",
    image_data: "",
    tags: []
  });

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleImageFile(files[0]);
    }
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleImageFile(file);
    }
  };

  const handleImageFile = async (file) => {
    if (!file.type.startsWith("image/") && !isHeicImage(file)) {
      toast.error("Please select an image file");
      return;
    }
    if (isHeicImage(file)) {
      toast.error("HEIC images aren't supported", { description: HEIC_HELP });
      return;
    }

    // Compressed JPEG for storage + preview (avoids bloating MongoDB documents)
    try {
      const compressed = await compressImageFile(file);
      setPreviewImage(compressed);
      setRecipePhoto(compressed);
    } catch (e) {
      // Fall back to raw read if canvas compression fails (e.g. HEIC on Safari)
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
        setRecipePhoto(e.target.result);
      };
      reader.readAsDataURL(file);
    }

    // Send the ORIGINAL high-quality file to OpenAI vision for extraction —
    // compressed pixels can hurt OCR.
    setIsExtracting(true);
    try {
      const base64 = await fileToBase64(file);
      const response = await axios.post(`${API}/recipes/extract`, {
        image_base64: base64,
        mime_type: file.type
      });

      applyExtractedData(response.data);
      toast.success("Recipe extracted successfully!", {
        description: "Review and edit the details before saving."
      });
      setActiveTab("manual");
    } catch (error) {
      console.error("Extraction error:", error);
      toast.error("Failed to extract recipe", {
        description: error.response?.data?.detail || "Please try again or enter manually."
      });
    } finally {
      setIsExtracting(false);
    }
  };

  const handleTextExtract = async () => {
    if (!pastedText.trim()) {
      toast.error("Please paste some recipe text first");
      return;
    }

    setIsExtracting(true);
    try {
      const response = await axios.post(`${API}/recipes/extract-text`, {
        recipe_text: pastedText
      });

      applyExtractedData(response.data);
      toast.success("Recipe extracted successfully!", {
        description: "Review and edit the details before saving."
      });
      setActiveTab("manual");
    } catch (error) {
      console.error("Text extraction error:", error);
      toast.error("Failed to extract recipe", {
        description: error.response?.data?.detail || "Please try again or enter manually."
      });
    } finally {
      setIsExtracting(false);
    }
  };

  const handleUrlExtract = async () => {
    if (!recipeUrl.trim()) {
      toast.error("Please enter a recipe URL first");
      return;
    }

    // Basic URL validation
    try {
      new URL(recipeUrl);
    } catch {
      toast.error("Please enter a valid URL");
      return;
    }

    setIsExtracting(true);
    try {
      const response = await axios.post(`${API}/recipes/extract-url`, {
        url: recipeUrl
      });

      applyExtractedData(response.data);
      toast.success("Recipe imported successfully!", {
        description: "Review and edit the details before saving."
      });
      setActiveTab("manual");
    } catch (error) {
      console.error("URL extraction error:", error);
      toast.error("Failed to import recipe", {
        description: error.response?.data?.detail || "The website may not be accessible or doesn't contain a recipe."
      });
    } finally {
      setIsExtracting(false);
    }
  };

  const applyExtractedData = (extracted) => {
    setFormData(prev => ({
      ...prev,
      title: extracted.title || "",
      source: extracted.source || "",
      category: extracted.category || "",
      servings: extracted.servings?.toString() || "",
      prep_time: extracted.prep_time || "",
      cook_time: extracted.cook_time || "",
      ingredients: extracted.ingredients?.length > 0 
        ? extracted.ingredients 
        : [{ ...emptyIngredient }],
      instructions: extracted.instructions?.length > 0 
        ? extracted.instructions 
        : [""],
      notes: extracted.notes || ""
    }));
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Handle recipe photo upload/capture (compresses before storing)
  const handlePhotoSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (isHeicImage(file)) {
      toast.error("HEIC images aren't supported", { description: HEIC_HELP });
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    try {
      const compressed = await compressImageFile(file);
      setRecipePhoto(compressed);
      setFormData(prev => ({ ...prev, image_data: compressed }));
    } catch (err) {
      // Canvas compression failed — fall back to raw read
      const reader = new FileReader();
      reader.onload = (ev) => {
        setRecipePhoto(ev.target.result);
        setFormData(prev => ({ ...prev, image_data: ev.target.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setRecipePhoto(null);
    setFormData(prev => ({ ...prev, image_data: "" }));
    if (photoInputRef.current) {
      photoInputRef.current.value = "";
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleIngredientChange = (index, field, value) => {
    const newIngredients = [...formData.ingredients];
    newIngredients[index] = { ...newIngredients[index], [field]: value };
    setFormData((prev) => ({ ...prev, ingredients: newIngredients }));
  };

  const addIngredient = () => {
    setFormData((prev) => ({
      ...prev,
      ingredients: [...prev.ingredients, { ...emptyIngredient }]
    }));
  };

  const removeIngredient = (index) => {
    if (formData.ingredients.length > 1) {
      setFormData((prev) => ({
        ...prev,
        ingredients: prev.ingredients.filter((_, i) => i !== index)
      }));
    }
  };

  const handleInstructionChange = (index, value) => {
    const newInstructions = [...formData.instructions];
    newInstructions[index] = value;
    setFormData((prev) => ({ ...prev, instructions: newInstructions }));
  };

  const addInstruction = () => {
    setFormData((prev) => ({
      ...prev,
      instructions: [...prev.instructions, ""]
    }));
  };

  const removeInstruction = (index) => {
    if (formData.instructions.length > 1) {
      setFormData((prev) => ({
        ...prev,
        instructions: prev.instructions.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error("Please enter a recipe title");
      return;
    }
    if (!formData.category) {
      toast.error("Please select a category");
      return;
    }

    // Filter out empty ingredients and instructions
    const cleanedData = {
      ...formData,
      servings: formData.servings ? parseInt(formData.servings) : null,
      ingredients: formData.ingredients.filter((ing) => ing.name.trim()),
      instructions: formData.instructions.filter((inst) => inst.trim()),
      image_data: recipePhoto || formData.image_data || null
    };

    if (cleanedData.ingredients.length === 0) {
      toast.error("Please add at least one ingredient");
      return;
    }
    if (cleanedData.instructions.length === 0) {
      toast.error("Please add at least one instruction");
      return;
    }

    setIsSaving(true);
    try {
      const response = await axios.post(`${API}/recipes`, cleanedData, {
        headers: getAuthHeaders()
      });
      toast.success("Recipe saved successfully!");
      navigate(`/recipe/${response.data.id}`);
    } catch (error) {
      console.error("Save error:", error);
      if (error.response?.status === 401) {
        toast.error("Authentication required", {
          description: "Please enter the admin password."
        });
        setPasswordDialogOpen(true);
      } else {
        toast.error("Failed to save recipe", {
          description: error.response?.data?.detail || "Please try again."
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Password Dialog */}
      <PasswordDialog 
        open={passwordDialogOpen} 
        onOpenChange={(open) => {
          setPasswordDialogOpen(open);
          if (!open && !isAuthenticated) {
            navigate('/');
          }
        }}
      />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="icon" className="rounded-full" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </Button>
          </Link>
          <Link to="/" className="flex items-center">
            <img
              src="/logo.png"
              alt="Burnsy's Bistro"
              className="h-12 w-auto object-contain"
            />
          </Link>
          <div className="ml-auto">
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8 rounded-full h-12 p-1 bg-secondary">
            <TabsTrigger 
              value="upload" 
              className="rounded-full data-[state=active]:bg-background data-[state=active]:shadow-sm gap-2"
              data-testid="tab-upload"
            >
              <Sparkles className="w-4 h-4" strokeWidth={1.5} />
              AI Extract
            </TabsTrigger>
            <TabsTrigger 
              value="manual" 
              className="rounded-full data-[state=active]:bg-background data-[state=active]:shadow-sm gap-2"
              data-testid="tab-manual"
            >
              <PenLine className="w-4 h-4" strokeWidth={1.5} />
              Review & Save
            </TabsTrigger>
          </TabsList>

          {/* AI Upload Tab */}
          <TabsContent value="upload" className="animate-fade-in">
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="font-serif text-2xl">Import Recipe with AI</CardTitle>
                <p className="text-muted-foreground">
                  Upload an image or paste recipe text — AI will extract all the details for you.
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Mode Toggle */}
                <div className="flex flex-wrap gap-2 p-1 bg-secondary rounded-full w-fit">
                  <button
                    type="button"
                    onClick={() => setExtractMode("image")}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                      extractMode === "image" 
                        ? "bg-background shadow-sm text-foreground" 
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid="mode-image"
                  >
                    <ImageIcon className="w-4 h-4" strokeWidth={1.5} />
                    Upload Image
                  </button>
                  <button
                    type="button"
                    onClick={() => setExtractMode("url")}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                      extractMode === "url" 
                        ? "bg-background shadow-sm text-foreground" 
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid="mode-url"
                  >
                    <LinkIcon className="w-4 h-4" strokeWidth={1.5} />
                    From URL
                  </button>
                  <button
                    type="button"
                    onClick={() => setExtractMode("text")}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                      extractMode === "text" 
                        ? "bg-background shadow-sm text-foreground" 
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid="mode-text"
                  >
                    <FileText className="w-4 h-4" strokeWidth={1.5} />
                    Paste Text
                  </button>
                </div>

                {/* Image Upload Mode */}
                {extractMode === "image" && (
                  <div
                    className={`upload-zone relative rounded-2xl p-12 text-center cursor-pointer transition-all ${
                      dragOver ? "drag-over border-primary bg-secondary" : ""
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="upload-zone"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="hidden"
                      data-testid="file-input"
                    />
                    
                    {isExtracting ? (
                      <div className="flex flex-col items-center gap-4">
                        <Loader2 className="w-12 h-12 text-primary animate-spin" strokeWidth={1.5} />
                        <p className="text-lg font-medium">Extracting recipe...</p>
                        <p className="text-sm text-muted-foreground">
                          Our AI is reading your recipe image
                        </p>
                      </div>
                    ) : previewImage ? (
                      <div className="space-y-4">
                        <img
                          src={previewImage}
                          alt="Recipe preview"
                          className="max-h-64 mx-auto rounded-xl shadow-md"
                        />
                        <p className="text-muted-foreground">Click to upload a different image</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center">
                          <Camera className="w-8 h-8 text-muted-foreground" strokeWidth={1.5} />
                        </div>
                        <div>
                          <p className="text-lg font-medium">Take a photo or upload image</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            Tap to use camera or select from library
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Text Paste Mode */}
                {extractMode === "text" && (
                  <div className="space-y-4">
                    <Textarea
                      value={pastedText}
                      onChange={(e) => setPastedText(e.target.value)}
                      placeholder="Paste your recipe text here...

Example:
Chocolate Chip Cookies

Ingredients:
2 cups all-purpose flour
1 cup butter, softened
3/4 cup sugar
...

Instructions:
1. Preheat oven to 375°F
2. Mix flour and butter..."
                      className="min-h-[300px] font-mono text-sm"
                      data-testid="text-input"
                    />
                    <Button
                      type="button"
                      onClick={handleTextExtract}
                      disabled={isExtracting || !pastedText.trim()}
                      className="w-full rounded-full h-12 gap-2"
                      data-testid="extract-text-btn"
                    >
                      {isExtracting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Extracting...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                          Extract Recipe with AI
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {/* URL Mode */}
                {extractMode === "url" && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="recipe-url">Recipe URL</Label>
                      <Input
                        id="recipe-url"
                        type="url"
                        value={recipeUrl}
                        onChange={(e) => setRecipeUrl(e.target.value)}
                        placeholder="https://www.example.com/recipe/chocolate-chip-cookies"
                        className="h-12"
                        data-testid="url-input"
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={handleUrlExtract}
                      disabled={isExtracting || !recipeUrl.trim()}
                      className="w-full rounded-full h-12 gap-2"
                      data-testid="extract-url-btn"
                    >
                      {isExtracting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Importing recipe...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                          Import Recipe from URL
                        </>
                      )}
                    </Button>
                  </div>
                )}

                <div className="p-4 bg-secondary/50 rounded-xl">
                  <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                    {extractMode === "image" ? (
                      <><Camera className="w-4 h-4" strokeWidth={1.5} /> Supported formats</>
                    ) : extractMode === "url" ? (
                      <><LinkIcon className="w-4 h-4" strokeWidth={1.5} /> Tips for importing</>
                    ) : (
                      <><FileText className="w-4 h-4" strokeWidth={1.5} /> Tips for best results</>
                    )}
                  </h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {extractMode === "image" ? (
                      <>
                        <li>• Take a photo of a recipe card or cookbook page</li>
                        <li>• Screenshots from social media (TikTok, Instagram, Pinterest)</li>
                        <li>• Typed or handwritten recipes</li>
                      </>
                    ) : extractMode === "url" ? (
                      <>
                        <li>• Works best with popular recipe websites</li>
                        <li>• Paste the full URL including https://</li>
                        <li>• Some websites may block scraping</li>
                      </>
                    ) : (
                      <>
                        <li>• Include the recipe title at the top</li>
                        <li>• Separate ingredients from instructions</li>
                        <li>• Copy/paste works great from websites, emails, or notes</li>
                      </>
                    )}
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Manual Entry / Review Tab */}
          <TabsContent value="manual" className="animate-fade-in">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Recipe Photo */}
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle className="font-serif text-xl">Recipe Photo</CardTitle>
                </CardHeader>
                <CardContent>
                  {recipePhoto ? (
                    <div className="relative inline-block">
                      <img
                        src={recipePhoto}
                        alt="Recipe"
                        className="max-h-48 rounded-xl shadow-md"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute -top-2 -right-2 w-8 h-8 rounded-full"
                        onClick={removePhoto}
                        data-testid="remove-photo-btn"
                      >
                        <X className="w-4 h-4" strokeWidth={2} />
                      </Button>
                    </div>
                  ) : (
                    <div
                      className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary transition-colors"
                      onClick={() => photoInputRef.current?.click()}
                      data-testid="photo-upload-zone"
                    >
                      <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoSelect}
                        className="hidden"
                        data-testid="photo-input"
                      />
                      <Camera className="w-10 h-10 mx-auto text-muted-foreground mb-3" strokeWidth={1.5} />
                      <p className="font-medium">Add a photo of your dish</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Take a photo or choose from library
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Basic Info */}
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle className="font-serif text-xl">Basic Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="title">Recipe Title *</Label>
                      <Input
                        id="title"
                        value={formData.title}
                        onChange={(e) => handleInputChange("title", e.target.value)}
                        placeholder="e.g., Grandma's Apple Pie"
                        className="h-12"
                        data-testid="input-title"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="category">Category *</Label>
                      <Select
                        value={formData.category}
                        onValueChange={(value) => handleInputChange("category", value)}
                      >
                        <SelectTrigger className="h-12" data-testid="select-category">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map((cat) => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="source">Source (optional)</Label>
                      <Input
                        id="source"
                        value={formData.source}
                        onChange={(e) => handleInputChange("source", e.target.value)}
                        placeholder="e.g., Mom's cookbook"
                        className="h-12"
                        data-testid="input-source"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="servings">Servings</Label>
                      <Input
                        id="servings"
                        type="number"
                        value={formData.servings}
                        onChange={(e) => handleInputChange("servings", e.target.value)}
                        placeholder="e.g., 8"
                        className="h-12"
                        data-testid="input-servings"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="prep_time">Prep Time</Label>
                      <Input
                        id="prep_time"
                        value={formData.prep_time}
                        onChange={(e) => handleInputChange("prep_time", e.target.value)}
                        placeholder="e.g., 20 minutes"
                        className="h-12"
                        data-testid="input-prep-time"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="cook_time">Cook Time</Label>
                      <Input
                        id="cook_time"
                        value={formData.cook_time}
                        onChange={(e) => handleInputChange("cook_time", e.target.value)}
                        placeholder="e.g., 45 minutes"
                        className="h-12"
                        data-testid="input-cook-time"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Ingredients */}
              <Card className="border-border/50">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-serif text-xl">Ingredients</CardTitle>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addIngredient}
                    className="rounded-full gap-1"
                    data-testid="add-ingredient-btn"
                  >
                    <Plus className="w-4 h-4" strokeWidth={2} />
                    Add
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  {formData.ingredients.map((ingredient, index) => (
                    <div key={index} className="grid gap-3 md:grid-cols-12 items-start p-4 bg-secondary/30 rounded-xl">
                      <div className="md:col-span-4 space-y-1">
                        <Label className="text-xs text-muted-foreground">Ingredient</Label>
                        <Input
                          value={ingredient.name}
                          onChange={(e) => handleIngredientChange(index, "name", e.target.value)}
                          placeholder="e.g., All-purpose flour"
                          data-testid={`ingredient-name-${index}`}
                        />
                      </div>
                      <div className="md:col-span-3 space-y-1">
                        <Label className="text-xs text-muted-foreground">Imperial</Label>
                        <Input
                          value={ingredient.amount_imperial}
                          onChange={(e) => handleIngredientChange(index, "amount_imperial", e.target.value)}
                          placeholder="e.g., 2 cups"
                          data-testid={`ingredient-imperial-${index}`}
                        />
                      </div>
                      <div className="md:col-span-3 space-y-1">
                        <Label className="text-xs text-muted-foreground">Metric</Label>
                        <Input
                          value={ingredient.amount_metric}
                          onChange={(e) => handleIngredientChange(index, "amount_metric", e.target.value)}
                          placeholder="e.g., 240g"
                          data-testid={`ingredient-metric-${index}`}
                        />
                      </div>
                      <div className="md:col-span-2 flex items-end">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeIngredient(index)}
                          className="rounded-full text-muted-foreground hover:text-destructive mt-5"
                          disabled={formData.ingredients.length === 1}
                          data-testid={`remove-ingredient-${index}`}
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Instructions */}
              <Card className="border-border/50">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-serif text-xl">Instructions</CardTitle>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addInstruction}
                    className="rounded-full gap-1"
                    data-testid="add-instruction-btn"
                  >
                    <Plus className="w-4 h-4" strokeWidth={2} />
                    Add Step
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  {formData.instructions.map((instruction, index) => (
                    <div key={index} className="flex gap-3 items-start">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                        <span className="text-sm font-medium text-primary">{index + 1}</span>
                      </div>
                      <Textarea
                        value={instruction}
                        onChange={(e) => handleInstructionChange(index, e.target.value)}
                        placeholder={`Step ${index + 1}...`}
                        className="flex-1 min-h-[80px]"
                        data-testid={`instruction-${index}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeInstruction(index)}
                        className="rounded-full text-muted-foreground hover:text-destructive flex-shrink-0"
                        disabled={formData.instructions.length === 1}
                        data-testid={`remove-instruction-${index}`}
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Notes */}
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle className="font-serif text-xl">Notes (optional)</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => handleInputChange("notes", e.target.value)}
                    placeholder="Any tips, variations, or additional notes..."
                    className="min-h-[120px]"
                    data-testid="input-notes"
                  />
                </CardContent>
              </Card>

              {/* Tags */}
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle className="font-serif text-xl">Tags (optional)</CardTitle>
                </CardHeader>
                <CardContent>
                  <TagInput
                    value={formData.tags}
                    onChange={(tags) => handleInputChange("tags", tags)}
                    placeholder="e.g. vegetarian, quick, kid-friendly — Enter to add"
                  />
                </CardContent>
              </Card>

              {/* Submit */}
              <div className="flex gap-4 justify-end">
                <Link to="/">
                  <Button type="button" variant="outline" className="rounded-full px-8" data-testid="cancel-btn">
                    Cancel
                  </Button>
                </Link>
                <Button 
                  type="submit" 
                  className="rounded-full px-8 gap-2"
                  disabled={isSaving}
                  data-testid="save-recipe-btn"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save Recipe"
                  )}
                </Button>
              </div>
            </form>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
